import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';

import { localDay } from '@app/utils/helpers/date-utils';
import { toFixedHours } from '@app/utils';
import { ProductStatus, ReportEntity } from '@data';
import { useListProducts } from '@data/hooks/products';
import { Button } from '@ui/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@ui/components/ui/dialog';
import { Input } from '@ui/components/ui/input';
import { Label } from '@ui/components/ui/label';
import { MultiSelectField, matchesLabel } from '@ui/components/ui/multi-select';
import { Textarea } from '@ui/components/ui/textarea';

import { checkHours } from './validateHours';

export interface EditEntryDialogProps {
  /** The ordinary entry being changed; nothing while closed. */
  report: ReportEntity | null;
  onHide: () => void;
  organizationId: string;
  userId?: string;
  onSubmit: (values: Partial<ReportEntity>) => void;
  /** Products holding another entry that day, which this one may not move onto. */
  takenProductIds?: string[];
}

/** One entry's product, hours and description; its day and chapter stay as they were. */
const EditEntryDialog = ({
  report,
  onHide,
  organizationId,
  userId,
  onSubmit,
  takenProductIds,
}: EditEntryDialogProps): JSX.Element => {
  const { t } = useTranslation();
  const [productId, setProductId] = useState('');
  const [hours, setHours] = useState('');
  const [today, setToday] = useState('');
  const [error, setError] = useState<string | undefined>();

  const { data: productData } = useListProducts(organizationId, {
    statuses: [ProductStatus.active, ProductStatus.support],
  });

  useEffect(() => {
    if (!report) return;
    setProductId(report.involvement?.product?.id ?? '');
    setHours(String(toFixedHours(report.minutes / 60)));
    setToday(report.today ?? '');
    setError(undefined);
  }, [report]);

  const options = useMemo(() => {
    const own = report?.involvement?.product;
    const listed = (productData?.items ?? []).filter(product => !takenProductIds?.includes(product.id));
    // The entry's own product stays pickable even once it is no longer active.
    const withOwn = own && !listed.some(product => product.id === own.id) ? [own, ...listed] : listed;

    return withOwn.map(product => ({ value: product.id, label: product.name }));
  }, [productData, report, takenProductIds]);

  const save = (): void => {
    if (!report) return;
    // Hours saved elsewhere may not be a quarter of an hour; only a change has to be.
    const isSameHours = hours === String(toFixedHours(report.minutes / 60));
    const hoursError = isSameHours ? undefined : checkHours(['entry'], { entry: hours }, t).errors.entry;
    if (hoursError) {
      setError(hoursError);
      return;
    }
    if (!today.trim()) {
      setError(t('editEntry.describeIt'));
      return;
    }

    onSubmit({
      ...report,
      userId: userId ?? report.involvement?.membership?.user?.id,
      productId,
      role: report.involvement?.role,
      workday: report.workday,
      today: today.trim(),
      hours: Number(hours.replace(',', '.')),
    } as Partial<ReportEntity>);
  };

  const day = report ? localDay(report.workday) : null;

  return (
    <Dialog open={!!report} onOpenChange={next => (next ? undefined : onHide())}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader className="pr-8">
          <DialogTitle>
            {day ? t('editEntry.title', { day: format(day, 'EEEE') }) : null}
            {day ? (
              <span className="ml-2 text-sm font-normal text-muted-foreground">{format(day, 'd MMM yyyy')}</span>
            ) : null}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-[minmax(0,1fr)_7rem] items-start gap-3">
            <MultiSelectField
              single
              clearable={false}
              filter={matchesLabel}
              id="edit-entry-product"
              label={t('titles.product')}
              options={options}
              value={productId ? [productId] : []}
              onChange={next => next[0] && setProductId(next[0])}
              emptyLabel={t('select')}
              searchPlaceholder={t('searchProduct')}
              emptySearchLabel={t('noProductsFound')}
            />
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="edit-entry-hours">{t('editEntry.hours')}</Label>
              <Input
                id="edit-entry-hours"
                inputMode="decimal"
                className="tabular-nums"
                value={hours}
                onChange={event => setHours(event.target.value)}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="edit-entry-today">{t('editEntry.whatDidYouDo')}</Label>
            <Textarea id="edit-entry-today" rows={4} value={today} onChange={event => setToday(event.target.value)} />
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onHide}>
            {t('actions.cancel')}
          </Button>
          <Button onClick={save} disabled={!productId}>
            {t('actions.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditEntryDialog;
